/**
 * Movement sound designs for Counter Pump.
 *
 * Surface-specific footsteps with distinct auditory character:
 * - Concrete: deep thud + slight grit
 * - Metal: bright ring + clang
 * - Wood: hollow knock + creak
 * - Sand: soft swoosh (muffled)
 * - Tile: sharp click + echo
 * - Gravel: crunchy noise bursts
 *
 * Plus jump (spring-like) and land (heavy thud).
 * All footsteps are < 150ms, jump < 100ms.
 */

import type { SoundDesign, SoundLayer } from './sound-designer';

const SR = 44100;

export type FootstepSpeedMode = 'creep' | 'walk' | 'sprint';

export function getFootstepGainForSpeed(mode: FootstepSpeedMode): number {
  switch (mode) {
    case 'creep': return 0.1;
    case 'walk': return 0.5;
    case 'sprint': return 1.0;
  }
}

function osc(
  freq: number,
  waveform: OscillatorType,
  gain: number,
  attack: number,
  decay: number,
  sustain: number,
  release: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'oscillator', waveform, frequency: freq,
    attack, decay, sustain, release, gain, ...opts,
  };
}

function noise(
  noiseType: 'white' | 'pink' | 'brown',
  gain: number,
  attack: number,
  decay: number,
  sustain: number,
  release: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'noise', noiseType, attack, decay, sustain, release, gain, ...opts,
  };
}

function fnoise(
  noiseType: 'white' | 'pink' | 'brown',
  filterType: BiquadFilterType,
  filterFreq: number,
  filterQ: number,
  gain: number,
  attack: number,
  decay: number,
  sustain: number,
  release: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'filtered-noise', noiseType, filterType, filterFreq, filterQ,
    attack, decay, sustain, release, gain, ...opts,
  };
}

function impulse(
  gain: number,
  duration: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'impulse', attack: 0, decay: 0, sustain: 1, release: 0,
    gain, duration, ...opts,
  };
}

export function getMovementSoundDesigns(): Map<string, SoundDesign> {
  const designs = new Map<string, SoundDesign>();

  // ─── CONCRETE ─────────────────────────────────────────────────────
  const concreteDesign: SoundDesign = {
    name: 'footstep_concrete',
    duration: 0.08,
    sampleRate: SR,
    layers: [
      impulse(0.35, 0.004),
      osc(80, 'sine', 0.3, 0.002, 0.02, 0.08, 0.025),
      osc(200, 'triangle', 0.12, 0.002, 0.01, 0.05, 0.02),
      fnoise('pink', 'lowpass', 1500, 1, 0.15, 0.001, 0.015, 0.06, 0.02),
    ],
    compression: { threshold: 0.5, ratio: 2.5, attack: 0.001, release: 0.01 },
  };
  designs.set('footstep_concrete', concreteDesign);

  // Legacy alias
  designs.set('footstep', { ...concreteDesign, name: 'footstep' });

  // ─── METAL ────────────────────────────────────────────────────────
  designs.set('footstep_metal', {
    name: 'footstep_metal',
    duration: 0.09,
    sampleRate: SR,
    layers: [
      impulse(0.4, 0.003),
      osc(800, 'sine', 0.25, 0.001, 0.02, 0.06, 0.03),
      osc(1600, 'sine', 0.12, 0.001, 0.015, 0.04, 0.025),
      osc(2400, 'sine', 0.06, 0.001, 0.01, 0.03, 0.02),
      osc(120, 'sine', 0.15, 0.002, 0.015, 0.05, 0.02),
      fnoise('white', 'highpass', 3000, 2, 0.08, 0.001, 0.01, 0.04, 0.02),
    ],
    compression: { threshold: 0.45, ratio: 3, attack: 0.001, release: 0.01 },
  });

  // ─── WOOD ─────────────────────────────────────────────────────────
  designs.set('footstep_wood', {
    name: 'footstep_wood',
    duration: 0.07,
    sampleRate: SR,
    layers: [
      impulse(0.35, 0.004),
      osc(200, 'sine', 0.28, 0.002, 0.018, 0.06, 0.02),
      osc(400, 'triangle', 0.12, 0.001, 0.01, 0.04, 0.015),
      fnoise('pink', 'bandpass', 800, 3, 0.1, 0.002, 0.012, 0.04, 0.018),
    ],
    compression: { threshold: 0.5, ratio: 2.5, attack: 0.001, release: 0.01 },
  });

  // ─── SAND ─────────────────────────────────────────────────────────
  designs.set('footstep_sand', {
    name: 'footstep_sand',
    duration: 0.1,
    sampleRate: SR,
    layers: [
      osc(60, 'sine', 0.1, 0.005, 0.025, 0.04, 0.03),
      fnoise('brown', 'lowpass', 1000, 0.7, 0.2, 0.005, 0.025, 0.06, 0.03),
      fnoise('pink', 'bandpass', 600, 1, 0.1, 0.003, 0.02, 0.04, 0.025),
    ],
    lowpass: 3000,
    compression: { threshold: 0.5, ratio: 2, attack: 0.002, release: 0.015 },
  });

  // ─── TILE ─────────────────────────────────────────────────────────
  designs.set('footstep_tile', {
    name: 'footstep_tile',
    duration: 0.075,
    sampleRate: SR,
    layers: [
      impulse(0.5, 0.003),
      osc(600, 'square', 0.2, 0.001, 0.008, 0.04, 0.015),
      osc(1200, 'sine', 0.08, 0.001, 0.006, 0.03, 0.015),
      osc(150, 'sine', 0.15, 0.002, 0.012, 0.05, 0.02),
      fnoise('white', 'highpass', 2500, 2, 0.06, 0.001, 0.006, 0.03, 0.015),
    ],
    reverb: { duration: 0.08, decay: 0.3, wet: 0.2 },
    compression: { threshold: 0.5, ratio: 2.5, attack: 0.001, release: 0.01 },
  });

  // ─── GRAVEL ───────────────────────────────────────────────────────
  designs.set('footstep_gravel', {
    name: 'footstep_gravel',
    duration: 0.09,
    sampleRate: SR,
    layers: [
      impulse(0.3, 0.005),
      noise('white', 0.25, 0.001, 0.015, 0.08, 0.025),
      fnoise('pink', 'bandpass', 1500, 2, 0.15, 0.002, 0.012, 0.06, 0.025),
      fnoise('white', 'highpass', 2000, 1.5, 0.1, 0.003, 0.01, 0.05, 0.02,
        { startTime: 0.01, duration: 0.06 }),
      osc(100, 'sine', 0.12, 0.003, 0.015, 0.04, 0.02),
    ],
    compression: { threshold: 0.45, ratio: 3, attack: 0.001, release: 0.01 },
  });

  // ─── JUMP ─────────────────────────────────────────────────────────
  designs.set('jump', {
    name: 'jump',
    duration: 0.06,
    sampleRate: SR,
    layers: [
      osc(200, 'sine', 0.2, 0.002, 0.01, 0.08, 0.015, { endFrequency: 500 }),
      fnoise('white', 'bandpass', 2000, 1.5, 0.15, 0.001, 0.01, 0.06, 0.015),
      osc(120, 'triangle', 0.1, 0.002, 0.01, 0.05, 0.012),
    ],
    compression: { threshold: 0.5, ratio: 2.5, attack: 0.001, release: 0.008 },
  });

  // ─── LAND ─────────────────────────────────────────────────────────
  designs.set('land', {
    name: 'land',
    duration: 0.1,
    sampleRate: SR,
    layers: [
      impulse(0.55, 0.006),
      osc(60, 'sine', 0.35, 0.002, 0.025, 0.08, 0.03),
      osc(150, 'triangle', 0.15, 0.002, 0.015, 0.05, 0.025),
      fnoise('pink', 'lowpass', 2000, 1, 0.18, 0.002, 0.015, 0.06, 0.03),
      noise('brown', 0.08, 0.003, 0.02, 0.04, 0.025),
    ],
    compression: { threshold: 0.45, ratio: 3, attack: 0.001, release: 0.012 },
  });

  return designs;
}
